/**
 * لیست زبان‌های RTL
 */
const RTL_LANGUAGES = ["fa", "ar", "ar_ae", "ar_iq"];

export function isRTL(lang) {
  if (!lang || typeof lang !== "string") {
    return false;
  }

  const normalizedLang = lang.toLowerCase().trim();

  if (RTL_LANGUAGES.includes(normalizedLang)) {
    return true;
  }

  if (normalizedLang.startsWith("ar")) {
    return true;
  }

  return false;
}

export function getTextDirection(lang) {
  return isRTL(lang) ? "rtl" : "ltr";
}

export function getDirectionClass(lang) {
  return getTextDirection(lang);
}

export function detectLanguage(text, fallbackLang = "fa") {
  if (!text || typeof text !== "string") {
    return getTextDirection(fallbackLang);
  }

  const cleanText = text.replace(/<[^>]*>/g, "").trim();

  if (!cleanText) {
    return getTextDirection(fallbackLang);
  }
  const persianRegex =
    /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g;
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F]/g;
  const englishRegex = /[a-zA-Z]/g;
  const numbersRegex = /[0-9\u0660-\u0669\u06F0-\u06F9]/g;

  const persianMatches = cleanText.match(persianRegex) || [];
  const arabicMatches = cleanText.match(arabicRegex) || [];
  const englishMatches = cleanText.match(englishRegex) || [];
  const numberMatches = cleanText.match(numbersRegex) || [];

  const rtlCharCount = persianMatches.length + arabicMatches.length;
  const ltrCharCount = englishMatches.length;
  const totalChars = cleanText.length - numberMatches.length;

  if (rtlCharCount === 0 && ltrCharCount === 0) {
    return getTextDirection(fallbackLang);
  }

  if (rtlCharCount > 0 && ltrCharCount === 0) {
    return "rtl";
  }

  if (ltrCharCount > 0 && rtlCharCount === 0) {
    return "ltr";
  }

  const rtlPercentage = (rtlCharCount / (rtlCharCount + ltrCharCount)) * 100;

  return rtlPercentage >= 50 ? "rtl" : "ltr";
}

export function getTextAlign(text, fallbackLang = "fa") {
  const direction = detectLanguage(text, fallbackLang);
  return direction === "rtl" ? "right" : "left";
}

export const textDirectionMixin = {
  computed: {
    isCurrentLangRTL() {
      const lang = this.currentLang || this.$i18n?.locale || "fa";
      return isRTL(lang);
    },
    currentTextDirection() {
      const lang = this.currentLang || this.$i18n?.locale || "fa";
      return getTextDirection(lang);
    },
    oppositeTextDirection() {
      return this.currentTextDirection === "rtl" ? "ltr" : "rtl";
    },

    textAlignStart() {
      return this.currentTextDirection === "rtl" ? "right" : "left";
    },

    textAlignEnd() {
      return this.currentTextDirection === "rtl" ? "left" : "right";
    },
  },

  methods: {
    isRTL(lang) {
      return isRTL(lang);
    },
    getTextDirection(lang) {
      return getTextDirection(lang);
    },
    /**
     * تشخیص زبان متن و برگرداندن direction
     * @param {string} text - متن برای تحلیل
     * @param {string} fallbackLang - زبان پیش‌فرض (اختیاری)
     * @returns {string} - 'rtl' یا 'ltr'
     */
    detectLanguage(text, fallbackLang) {
      const defaultFallback =
        this.currentLang || this.$i18n?.locale || fallbackLang || "fa";
      return detectLanguage(text, defaultFallback);
    },
    /**
     * دریافت text-align مناسب بر اساس متن
     * @param {string} text - متن برای تحلیل
     * @param {string} fallbackLang - زبان پیش‌فرض (اختیاری)
     * @returns {string} - 'right' یا 'left'
     */
    getTextAlign(text, fallbackLang) {
      return getTextAlign(text, fallbackLang);
    },
  },
};

export const textDirectionPlugin = {
  install(Vue) {
    Vue.prototype.$isRTL = isRTL;
    Vue.prototype.$getTextDirection = getTextDirection;
    Vue.prototype.$getDirectionClass = getDirectionClass;
    Vue.prototype.$detectLanguage = detectLanguage;
    Vue.prototype.$getTextAlign = getTextAlign;
  },
};

export default {
  isRTL,
  getTextDirection,
  getDirectionClass,
  detectLanguage,
  getTextAlign,
  textDirectionMixin,
  textDirectionPlugin,
};
